import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import BackdropCircularProgress from '../../../components/BackdropCircularProgress';
import JobCreationFAQ from '../../../components/FAQcomponent';
import FormCloseButton from '../../../components/Form/FormCloseButton';
import selectUser from '../../../store/selectors/appSelector';
import { PRIMARY_COLOR } from '../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../theme/fontConstants';
import { showSnackbar } from '../../app';
import { ROUTE_CONSTANTS } from '../../app/RouteBuilding/routeConstants';
import { FAQ_LIST } from '../constDicts';

import CreateJobFormComponent from './components/CreateJobFormComponent';
import useCreateRecurringTwoWeeksDayJobs from './hooks/useCreateRecurringTwoWeeksDayJobs';

const recurringTwoWeeksJobCreateStyles = {
  headerTypography: {
    color: PRIMARY_COLOR[70],
    fontSize: '24px',
    fontFamily: PRIMARY_FONT[700],
    marginBottom: '8px',
  },
  subheaderRowSx: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px',
  },
  subheaderTypographySx: {
    color: PRIMARY_COLOR[70],
    fontSize: '16px',
    fontFamily: PRIMARY_FONT[300],
  },
  closeButtonContainerSx: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginBottom: '16px',
  },
};

const RecurringTwoWeeksJobCreate = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector(selectUser);
  const theme = useTheme();

  const closeAndClear = useCallback(
    (message) => {
      navigate(`/${ROUTE_CONSTANTS.JOB_ORDERS}`);
      if (message) {
        // Optional: callers can still pass a message key to show after navigation.
        dispatch(showSnackbar({ message }));
      }
    },
    [navigate, dispatch]
  );

  const { createJobOrders, isCreatingJobs } = useCreateRecurringTwoWeeksDayJobs({
    onSuccess: () => closeAndClear(),
  });

  const stackedLayout = useMediaQuery(theme.breakpoints.down('md'));

  const mainWrapperSx = {
    width: '100%',
    maxWidth: '1600px',
    display: 'flex',
    flexDirection: stackedLayout ? 'column' : 'row',
    gap: '40px',
    justifyContent: 'center',
  };

  const jobCreationSectionSx = {
    width: '100%',
    maxWidth: '960px',
    flex: stackedLayout ? '1 1 auto' : '0 1 960px',
    minWidth: 0,
    marginLeft: 'auto',
    marginRight: 'auto',
  };

  const faqSectionSx = {
    width: '100%',
    maxWidth: '600px',
    flex: stackedLayout ? '1 1 auto' : '0 1 600px',
    minWidth: 0,
    marginLeft: 'auto',
    marginRight: 'auto',
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        zIndex: 10,
        width: '100%',
        padding: theme.spacing(2),
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      {isCreatingJobs && <BackdropCircularProgress />}
      <Box sx={mainWrapperSx}>
        <Box sx={jobCreationSectionSx}>
          <Box sx={recurringTwoWeeksJobCreateStyles.closeButtonContainerSx}>
            <FormCloseButton onClose={closeAndClear} message="jobs.backToList" />
          </Box>

          <Typography sx={recurringTwoWeeksJobCreateStyles.headerTypography}>
            {LanguageConverterV2('job.create.recurringTwoWeeks.header')}
          </Typography>
          <Box sx={recurringTwoWeeksJobCreateStyles.subheaderRowSx}>
            <CalendarTodayOutlinedIcon sx={{ color: PRIMARY_COLOR[70] }} />
            <Typography sx={recurringTwoWeeksJobCreateStyles.subheaderTypographySx}>
              {LanguageConverterV2('job.create.recurringTwoWeeks.subheader')}
            </Typography>
          </Box>
          <CreateJobFormComponent
            user={user}
            onSubmit={createJobOrders}
            onClose={closeAndClear}
            isProcessing={isCreatingJobs}
          />
        </Box>

        <Box sx={faqSectionSx}>
          <JobCreationFAQ faqList={FAQ_LIST} />
        </Box>
      </Box>
    </Box>
  );
};

export default RecurringTwoWeeksJobCreate;
